import {
  CaseReducerActions,
  createSlice,
  PayloadAction,
  Slice,
  SliceCaseReducers,
  ValidateSliceCaseReducers,
} from '@reduxjs/toolkit';

type PayloadSetState<T> = PayloadAction<(state: T) => void>;
type ReducersType<T extends Record<string, Slice>> = { [K in keyof T]: T[K]['reducer']; }
type ActionsType<T extends Record<string, Slice>> = { [K in keyof T]: T[K]['actions']; }

export const bindReducers = <T extends Record<string, Slice>>(slices: T) => Object.fromEntries(
  Object.entries(slices).map(([n, { reducer }]) => [n, reducer]),
) as ReducersType<T>;

export const bindActions = <T extends Record<string, Slice>>(slices: T) => Object.fromEntries(
  Object.entries(slices).map(([n, { actions }]) => [n, actions]),
) as ActionsType<T>;

type SetterName<Name extends string = string> = `set${Capitalize<Name>}`;

/* eslint-disable-next-line max-len */
const getSetterName = <Name extends string = string>(str: Name): SetterName<Name> => `set${str.charAt(0).toUpperCase()}${str.slice(1)}` as any;

/* eslint-disable-next-line max-len */
export const createSliceSteamup = <State, CaseReducers extends SliceCaseReducers<State> = SliceCaseReducers<State>, Name extends string = string>(
  name: Name,
  initialState: State,
  reducers: ValidateSliceCaseReducers<State, CaseReducers> = {} as any,
) => {
  if (typeof initialState === 'object') {
    Object.keys(initialState as any).forEach((key) => {
      const setterName = getSetterName(key);
      (reducers as any)[setterName] = (state: any, { payload }: any) => {
        state[key] = payload;
      };
    });
  }

  const slice = createSlice({
    name,
    initialState,
    reducers: {
      init: () => initialState,
      setState(state, { payload }: PayloadSetState<typeof state>) {
        payload(state);
      },
      ...reducers,
    },
  });

  return slice as typeof slice & {
    actions: (typeof slice.actions) & (CaseReducerActions<CaseReducers, Name>) & ({
      [K in keyof State as SetterName<string & K>]: (payload: State[K]) => PayloadAction<any>;
    })
  };
};
